import { GraphQLResult } from '@aws-amplify/api-graphql';
import { Signer } from '@aws-amplify/core';
import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { captureException } from '@sentry/react';

import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';
import type {
  SSOConfiguration,
  SSOMetadataValidationResponse,
  SSOOperationResponse,
  VerifySSODomainResponse,
} from 'graphql_custom/sso/types';
import { adminUpdateMemberPhoneMutation } from 'graphql_custom/team/adminUpdateMemberPhone';
import { adminVerifyMemberMutation } from 'graphql_custom/team/adminVerifyMember';
import type {
  AdminUpdateMemberInput,
  AdminUpdateMemberPhonePayload,
  AdminVerifyMemberPayload,
  AdminGetNotificationPreferencesInput,
  AdminSetGroupAdminsInput,
  AdminUpdateNotificationPreferencesInput,
  CheckSsoDomainInput,
  CheckSsoDomainResponse,
  CompleteBannerInput,
  CompleteBannerPayload,
  CompleteChecklistInput,
  CompleteChecklistPayload,
  CountUsersWithCustomFiltersInput,
  CountUsersWithCustomFiltersPayload,
  FeatureFlag,
  BasicHttpResponse,
  ListPaidolUserByUserIdInput,
  ListPaidolUserByUserIdPayload,
  ListPaidolUsersFilteredInput,
  ListPaidolUsersFilteredPayload,
  RemoveUserInput,
  RemoveUserPayload,
  ResetUserFiltersInput,
  ResetUserFiltersPayload,
  ResolveEmailToUsernameInput,
  SendWelcomeSmsInput,
  V2Paidol,
  UpdateMyPreferencesInput,
  ValidateEmailAndUsernameInput,
} from 'CDK_API';

// Re-export types used by other files
export type { FeatureFlag } from 'CDK_API';

// Hand-written types for the searchPaidolsAsSupport query — `yarn codegen:cdk`
// is failing in this branch (unrelated typescript-plugin issue), so we define
// them here to keep the PR self-contained until codegen is fixed.
export type SearchPaidolsAsSupportInput = {
  query: string;
  limit?: number | null;
  nextToken?: string | null;
};
export type SearchPaidolsAsSupportPayload = {
  items: Array<V2Paidol>;
  nextToken?: string | null;
};

// Re-export with aliases to preserve existing names used across the codebase
export type CheckSSODomainInput = CheckSsoDomainInput;
export type CheckSSODomainResponse = CheckSsoDomainResponse;
export type BasicHTTPResponse = BasicHttpResponse;
export type {
  SSOConfiguration,
  SSOMetadataValidationResponse,
  SSOOperationResponse,
  VerifySSODomainResponse,
} from 'graphql_custom/sso/types';
export type {
  AdminUpdateMemberInput,
  CountUsersWithCustomFiltersInput,
  CountUsersWithCustomFiltersPayload,
  ListPaidolUserByUserIdInput,
  ListPaidolUsersFilteredInput,
  RemoveUserInput,
  RemoveUserPayload,
  ResetUserFiltersInput,
  ResetUserFiltersPayload,
  ResolveEmailToUsernameInput,
  SendWelcomeSmsInput,
  UpdateMyPreferencesInput,
  ValidateEmailAndUsernameInput,
} from 'CDK_API';

// SEE-1232 rows k/l — the signed-in user's own profile. Source: app/store/userProfileQueries.ts
// (getUser / updateUser). updateUser's response replaces the whole profile, so it selects the
// same fields as getUser: the Amplify selection omitted is_support, is_speedster and
// firstRunBanner, which left them undefined after any profile save until reload.
// - getUser is not tenant-scoped. The owner-only fields (birthdate, address, phone_number) are
//   returned only when the caller's resolved id (custom:original_sub, else sub) equals `id` —
//   which it does here, since authSlice rewrites sub to custom:original_sub.
// - updateUser accepts only the owner's editable fields; phone_number is deliberately absent
//   (SEE-221), and no r2 caller sends it.
const getUserQuery = /* GraphQL */ `
  query GetUser($id: ID!) {
    getUser(id: $id) {
      address
      birthdate
      createdAt
      is_support
      email
      first_name
      middle_name
      id
      is_speedster
      last_name
      suffix
      phone_number
      phone_extension
      password_updated_at
      picture
      updatedAt
      username
      firstRunBanner {
        insights
        transactions
        reimbursements
        cardsManagement
        spendRules
        funding
        teamMembers
        groups
        wallet
        settings
        connections
      }
    }
  }
`;

const updateUserMutation = /* GraphQL */ `
  mutation UpdateUser($input: V2UpdateUserInput!) {
    updateUser(input: $input) {
      id
      username
      email
      first_name
      middle_name
      last_name
      suffix
      picture
      createdAt
      updatedAt
      address
      birthdate
      phone_number
      phone_extension
      password_updated_at
      is_support
      is_speedster
      firstRunBanner {
        insights
        transactions
        reimbursements
        cardsManagement
        spendRules
        funding
        teamMembers
        groups
        wallet
        settings
        connections
      }
    }
  }
`;

// SEE-1232 row m — the caller's OWN notification preferences. Source:
// graphql_custom/notifications.ts (GET_/CREATE_/UPDATE_USER_NOTIFICATION_PREFERENCES), same
// selection. The row is always the caller's (id == userId == caller): the read takes no
// arguments, `id`/`userId` are not in either input, and update resolves the row from the
// caller, never from the input. Admin reads/writes of another member's row stay on
// adminGet/adminUpdateNotificationPreferences (SEE-1312).
const getMyNotificationPreferencesQuery = /* GraphQL */ `
  query GetMyNotificationPreferences {
    getMyNotificationPreferences {
      id
      userId
      paidolId
      transactionAlertSms
      transactionAlertEmail
      cardLockedSms
      cardLockedEmail
      manualCardActionSms
      manualCardActionEmail
      cardActivationSms
      cardActivationEmail
      statementReadySms
      statementReadyEmail
      txnReviewFrequency
      txnReviewCodeUpdateSms
      txnReviewCodeUpdateEmail
      txnReviewNoteUpdateSms
      txnReviewNoteUpdateEmail
      txnReviewReceiptUpdateSms
      txnReviewReceiptUpdateEmail
      txnReviewResubmissionSms
      txnReviewResubmissionEmail
      txnReviewReadySms
      txnReviewReadyEmail
      returnedTransactionSms
      returnedTransactionEmail
      hasReceivedWelcomeSms
      createdAt
      updatedAt
    }
  }
`;

const createMyNotificationPreferencesMutation = /* GraphQL */ `
  mutation CreateMyNotificationPreferences($input: V2CreateMyNotificationPreferencesInput!) {
    createMyNotificationPreferences(input: $input) {
      id
      userId
      paidolId
      transactionAlertSms
      transactionAlertEmail
      cardLockedSms
      cardLockedEmail
      manualCardActionSms
      manualCardActionEmail
      cardActivationSms
      cardActivationEmail
      statementReadySms
      statementReadyEmail
      txnReviewFrequency
      txnReviewCodeUpdateSms
      txnReviewCodeUpdateEmail
      txnReviewNoteUpdateSms
      txnReviewNoteUpdateEmail
      txnReviewReceiptUpdateSms
      txnReviewReceiptUpdateEmail
      txnReviewResubmissionSms
      txnReviewResubmissionEmail
      txnReviewReadySms
      txnReviewReadyEmail
      returnedTransactionSms
      returnedTransactionEmail
      hasReceivedWelcomeSms
      createdAt
      updatedAt
    }
  }
`;

const updateMyNotificationPreferencesMutation = /* GraphQL */ `
  mutation UpdateMyNotificationPreferences($input: V2UpdateMyNotificationPreferencesInput!) {
    updateMyNotificationPreferences(input: $input) {
      id
      userId
      paidolId
      transactionAlertSms
      transactionAlertEmail
      cardLockedSms
      cardLockedEmail
      manualCardActionSms
      manualCardActionEmail
      cardActivationSms
      cardActivationEmail
      statementReadySms
      statementReadyEmail
      txnReviewFrequency
      txnReviewCodeUpdateSms
      txnReviewCodeUpdateEmail
      txnReviewNoteUpdateSms
      txnReviewNoteUpdateEmail
      txnReviewReceiptUpdateSms
      txnReviewReceiptUpdateEmail
      txnReviewResubmissionSms
      txnReviewResubmissionEmail
      txnReviewReadySms
      txnReviewReadyEmail
      returnedTransactionSms
      returnedTransactionEmail
      hasReceivedWelcomeSms
      createdAt
      updatedAt
    }
  }
`;

type AuthMode = 'cognito' | 'iam';

/**
 * AWSJSON scalar fields come back as JSON strings from AppSync typed returns.
 * This utility walks a response object and JSON.parse() any string values
 * for known AWSJSON field names, converting them back to objects.
 */
const AWSJSON_FIELDS = new Set([
  'firstRunChecklist',
  'firstRunBanner',
  'codingColumnsConfig',
  'codingRequirements',
  'columnPreferences',
  'sage300PostingSettings',
  'sageIntacctBillableSettings',
  'qboPostingSettings',
  'qboAnyCodingPostingSettings',
  'erpCostCodesUseDescriptionSetting',
  'erpDropdownSortByCodeSetting',
  'accountingDateSettings',
  'receiptAgentSettings',
  'receiptsSettings',
  'reimbursementSettings',
  'fuelCaptureSettings',
  'notesSettings',
  'editLockingSettings',
  'flexCodingSettings',
  'flexFieldSettings',
  'jobCodeSettings',
]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseAwsJsonFields(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(parseAwsJsonFields);
  if (typeof obj !== 'object') return obj;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (AWSJSON_FIELDS.has(key) && typeof value === 'string') {
      try {
        result[key] = JSON.parse(value);
      } catch {
        result[key] = value;
      }
    } else if (typeof value === 'object' || Array.isArray(value)) {
      result[key] = parseAwsJsonFields(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

// --- GraphQL Operations ---

const listFeatureFlagsQuery = /* GraphQL */ `
  query ListFeatureFlags($email: String!, $paidolId: String!) {
    listFeatureFlags(email: $email, paidolId: $paidolId) {
      name
      value
    }
  }
`;

const listPublicFeatureFlagsQuery = /* GraphQL */ `
  query ListPublicFeatureFlags {
    listPublicFeatureFlags {
      name
      value
    }
  }
`;

const validateEmailAndUsernameQuery = /* GraphQL */ `
  query ValidateEmailAndUsername($input: ValidateEmailAndUsernameInput!) {
    validateEmailAndUsername(input: $input)
  }
`;

const V2_USER_FRAGMENT = /* GraphQL */ `
  fragment V2UserFields on V2User {
    id
    __typename
    first_name
    middle_name
    last_name
    suffix
    email
    username
    picture
    phone_number
    address
    is_support
    createdAt
    updatedAt
    deletedAt
  }
`;

const V2_FILTER_SETTINGS_FRAGMENT = /* GraphQL */ `
  fragment V2FilterSettingsFields on V2FilterSettings {
    isAllAvailable
    ids
  }
`;

const V2_PAIDOL_USER_FIELDS = /* GraphQL */ `
  id
  __typename
  user_id
  email
  roles
  paidol_id
  createdAt
  updatedAt
  deletedAt
  employee_id
  glCodeVisibilitySettings { ...V2FilterSettingsFields }
  projectVisibilitySettings { ...V2FilterSettingsFields }
  departmentVisibilitySettings { ...V2FilterSettingsFields }
  columnPreferences
  widgetPreferences { widgetId hidden }
  landingPagePreference
  user { ...V2UserFields }
`;

const listPaidolUsersFilteredQuery = /* GraphQL */ `
  query ListPaidolUsersFiltered($input: listPaidolUsersFilteredInput!) {
    listPaidolUsersFiltered(input: $input) {
      items {
        ${V2_PAIDOL_USER_FIELDS}
      }
      nextToken
    }
  }
  ${V2_USER_FRAGMENT}
  ${V2_FILTER_SETTINGS_FRAGMENT}
`;

const listPaidolUserByUserIdQuery = /* GraphQL */ `
  query ListPaidolUserByUserId($input: listPaidolUserByUserIdInput!) {
    listPaidolUserByUserId(input: $input) {
      paidolUsersItems {
        ${V2_PAIDOL_USER_FIELDS}
        paidol {
          id
          __typename
          name
          slug
          type
          logoUrl
          enablePCards
          enablePayables
          isConstructionType
          isAgaveClient
          intacctProjectsNotLicensed
          intacctTopLevelProjectPosting
          isProcoreClient
          agaveAccountToken
          agave_data_sync_in_progress
          agaveSourceSystemSlug
          erpSpeedchain
          erpConnectionId
          low_balance_alert_threshold
          low_balance_auto_funding_amount
          low_balance_auto_funding_threshold
          firstRunChecklist
          glCodeVisibilitySettings { ...V2FilterSettingsFields }
          projectVisibilitySettings { ...V2FilterSettingsFields }
          locationVisibilitySettings { ...V2FilterSettingsFields }
          codingColumnsConfig
          codingRequirements
          widgetVisibilityConfig { widgetId hidden }
          erpProjectsEndpointSetting { selectedEndpoint isEnabled shouldDisplay }
          erpDepartmentLocationSetting { isEnabled departmentEnabled locationEnabled filterProjectsByEmployeeLocationEnabled }
          cardLockSettings { isEnabled daysToLock isReceiptsRequired isNotesRequired isFuelCaptureRequired applyImmediately disableAutoUnlock includePendingCharges }
          sessionTimeoutSettings { isEnabled timeoutMinutes }
          approvalWorkflowSettings { isEnabled approvalLevels groupApprovalsEnabled }
          editLockingSettings
          sage300PostingSettings
          sageIntacctBillableSettings
          qboPostingSettings
          qboAnyCodingPostingSettings
          erpCostCodesUseDescriptionSetting
          erpDropdownSortByCodeSetting
          accountingDateSettings
          receiptAgentSettings
          receiptsSettings
          reimbursementSettings
          fuelCaptureSettings
          notesSettings
          flexCodingSettings
          flexFieldSettings
          jobCodeSettings
          glCodeFilteringByUser { isEnabled }
          projectFilteringByUser { isEnabled }
          locationFilteringByUser { isEnabled }
          departmentFilteringByUser { isEnabled }
          requireProjectBeforeCostCode { isEnabled }
          scopeEquipmentCodesToEmDepartment { isEnabled }
          creditLimit
          paymentTermDays
          createdAt
          updatedAt
        }
        paymentCards {
          id
          __typename
          highnotePaymentCardID
          paidolUserID
          createdAt
          updatedAt
        }
        managedGroups {
          items {
            cardGroupId
            approvalLevels
            isReviewer
            cardGroup {
              id
              name
              hasReviewers
            }
          }
        }
        managedProjectsGroups {
          items {
            projectGroupId
            approvalLevels
            isReviewer
            projectGroup {
              id
              name
              hasReviewers
              groupProjects
            }
          }
        }
      }
      paidolItems {
        id
        __typename
        name
        slug
        type
        logoUrl
        enablePCards
        enablePayables
        isConstructionType
        isAgaveClient
        intacctProjectsNotLicensed
        intacctTopLevelProjectPosting
        isProcoreClient
        agaveAccountToken
        agave_data_sync_in_progress
        agaveSourceSystemSlug
        erpSpeedchain
        erpConnectionId
        low_balance_alert_threshold
        low_balance_auto_funding_amount
        low_balance_auto_funding_threshold
        firstRunChecklist
        glCodeVisibilitySettings { ...V2FilterSettingsFields }
        projectVisibilitySettings { ...V2FilterSettingsFields }
        locationVisibilitySettings { ...V2FilterSettingsFields }
        codingColumnsConfig
        codingRequirements
        widgetVisibilityConfig { widgetId hidden }
        erpProjectsEndpointSetting { selectedEndpoint isEnabled shouldDisplay }
        erpDepartmentLocationSetting { isEnabled departmentEnabled locationEnabled }
        cardLockSettings { isEnabled daysToLock isReceiptsRequired isNotesRequired isFuelCaptureRequired applyImmediately disableAutoUnlock }
        sessionTimeoutSettings { isEnabled timeoutMinutes }
        approvalWorkflowSettings { isEnabled approvalLevels groupApprovalsEnabled }
        editLockingSettings
        sage300PostingSettings
        sageIntacctBillableSettings
        qboPostingSettings
        qboAnyCodingPostingSettings
        erpCostCodesUseDescriptionSetting
        erpDropdownSortByCodeSetting
        accountingDateSettings
        receiptAgentSettings
        receiptsSettings
        reimbursementSettings
        fuelCaptureSettings
        notesSettings
        flexCodingSettings
        flexFieldSettings
        jobCodeSettings
        glCodeFilteringByUser { isEnabled }
        projectFilteringByUser { isEnabled }
        locationFilteringByUser { isEnabled }
        departmentFilteringByUser { isEnabled }
        requireProjectBeforeCostCode { isEnabled }
        scopeEquipmentCodesToEmDepartment { isEnabled }
        creditLimit
        paymentTermDays
        createdAt
        updatedAt
      }
    }
  }
  ${V2_USER_FRAGMENT}
  ${V2_FILTER_SETTINGS_FRAGMENT}
`;

const searchPaidolsAsSupportQuery = /* GraphQL */ `
  query SearchPaidolsAsSupport($input: SearchPaidolsAsSupportInput!) {
    searchPaidolsAsSupport(input: $input) {
      items {
        id
        __typename
        name
        slug
        type
        logoUrl
        enablePCards
        enablePayables
        isConstructionType
        isAgaveClient
        intacctProjectsNotLicensed
        intacctTopLevelProjectPosting
        isProcoreClient
        agaveAccountToken
        agave_data_sync_in_progress
        agaveSourceSystemSlug
        erpSpeedchain
        erpConnectionId
        low_balance_alert_threshold
        low_balance_auto_funding_amount
        low_balance_auto_funding_threshold
        firstRunChecklist
        glCodeVisibilitySettings {
          ...V2FilterSettingsFields
        }
        projectVisibilitySettings {
          ...V2FilterSettingsFields
        }
        locationVisibilitySettings {
          ...V2FilterSettingsFields
        }
        codingColumnsConfig
        codingRequirements
        widgetVisibilityConfig {
          widgetId
          hidden
        }
        erpProjectsEndpointSetting {
          selectedEndpoint
          isEnabled
          shouldDisplay
        }
        erpDepartmentLocationSetting {
          isEnabled
          departmentEnabled
          locationEnabled
        }
        cardLockSettings {
          isEnabled
          daysToLock
          isReceiptsRequired
          isNotesRequired
          isFuelCaptureRequired
          applyImmediately
          disableAutoUnlock
          includePendingCharges
        }
        sessionTimeoutSettings {
          isEnabled
          timeoutMinutes
        }
        approvalWorkflowSettings {
          isEnabled
          approvalLevels
          groupApprovalsEnabled
        }
        editLockingSettings
        sage300PostingSettings
        sageIntacctBillableSettings
        qboPostingSettings
        qboAnyCodingPostingSettings
        erpCostCodesUseDescriptionSetting
        erpDropdownSortByCodeSetting
        accountingDateSettings
        receiptAgentSettings
        receiptsSettings
        reimbursementSettings
        fuelCaptureSettings
        notesSettings
        flexCodingSettings
        flexFieldSettings
        jobCodeSettings
        glCodeFilteringByUser {
          isEnabled
        }
        projectFilteringByUser {
          isEnabled
        }
        locationFilteringByUser {
          isEnabled
        }
        departmentFilteringByUser {
          isEnabled
        }
        creditLimit
        paymentTermDays
        createdAt
        updatedAt
      }
      nextToken
    }
  }
  ${V2_FILTER_SETTINGS_FRAGMENT}
`;

const removeUserMutation = /* GraphQL */ `
  mutation RemoveUser($input: RemoveUserInput!) {
    removeUser(input: $input) {
      paidolUserId
      userId
    }
  }
`;

// SEE-856 D1 — admin-only mutation that replaces auto-gen updatePaidolUser for
// privileged member fields. Returns BasicHTTPResponse where `body` is the
// JSON-stringified updated PaidolUser dict on success or an error string on 4xx.
const adminUpdateMemberMutation = /* GraphQL */ `
  mutation AdminUpdateMember($input: AdminUpdateMemberInput!) {
    adminUpdateMember(input: $input) {
      statusCode
      body
    }
  }
`;

// SEE-1312: read half of the SEE-1059 admin notification-preferences path.
// UserNotificationPreferences reads are owner-scoped now, so the Team Members
// editor loads a member's row through this admin-asserted query.
const adminGetNotificationPreferencesQuery = /* GraphQL */ `
  query AdminGetNotificationPreferences($input: AdminGetNotificationPreferencesInput!) {
    adminGetNotificationPreferences(input: $input) {
      statusCode
      body
    }
  }
`;

const adminUpdateNotificationPreferencesMutation = /* GraphQL */ `
  mutation AdminUpdateNotificationPreferences($input: AdminUpdateNotificationPreferencesInput!) {
    adminUpdateNotificationPreferences(input: $input) {
      statusCode
      body
    }
  }
`;

// SEE-1314 — admin-only replacement for the auto-generated create/delete
// mutations on the group join tables: sets a group's full admin list.
const adminSetGroupAdminsMutation = /* GraphQL */ `
  mutation AdminSetGroupAdmins($input: AdminSetGroupAdminsInput!) {
    adminSetGroupAdmins(input: $input) {
      statusCode
      body
    }
  }
`;

// SEE-857 D2 — self-service mutation for the user's own non-privileged
// PaidolUser preferences. Same BasicHTTPResponse shape as adminUpdateMember.
const updateMyPreferencesMutation = /* GraphQL */ `
  mutation UpdateMyPreferences($input: UpdateMyPreferencesInput!) {
    updateMyPreferences(input: $input) {
      statusCode
      body
    }
  }
`;

// SEE-191: first-run state mutations, served by the CDK first-run-resolver
// Lambda (dispatches on input.operationName — payload shape unchanged from Amplify).
const completeFirstRunChecklistMutation = /* GraphQL */ `
  mutation CompleteFirstRunChecklist($input: CompleteChecklistInput!) {
    completeFirstRunChecklist(input: $input) {
      paidolId
    }
  }
`;

const completeFirstRunBannerMutation = /* GraphQL */ `
  mutation CompleteFirstRunBanner($input: CompleteBannerInput!) {
    completeFirstRunBanner(input: $input) {
      userId
    }
  }
`;

const resetUserFiltersMutation = /* GraphQL */ `
  mutation ResetUserFilters($input: ResetUserFiltersInput!) {
    resetUserFilters(input: $input) {
      success
      message
      usersUpdated
    }
  }
`;

const countUsersWithCustomFiltersMutation = /* GraphQL */ `
  mutation CountUsersWithCustomFilters($input: CountUsersWithCustomFiltersInput!) {
    countUsersWithCustomFilters(input: $input) {
      success
      count
      message
    }
  }
`;

const sendWelcomeSmsMutation = /* GraphQL */ `
  mutation SendWelcomeSms($input: SendWelcomeSmsInput!) {
    sendWelcomeSms(input: $input) {
      statusCode
      body
    }
  }
`;

// --- SEE-221 step-up MFA (bank linking) ---
// Resolved by the v2Security CDK function on the CDK AppSync API. Types are
// hand-written (not yet in `yarn codegen:cdk` output), matching the
// searchPaidolsAsSupport precedent.
export interface BankLinkStepUpChallenge {
  sent: boolean;
  maskedPhone: string | null;
  expiresInSeconds: number | null;
}

export interface BankLinkStepUpResult {
  verified: boolean;
  attemptsRemaining: number | null;
  /**
   * Short-lived signed grant (SEE-221), present only when `verified`. Passed on
   * the next Plaid link-token request so the resolver can confirm the step-up
   * server-side. Null/absent for a failed attempt.
   */
  grant: string | null;
}

const requestBankLinkStepUpMutation = /* GraphQL */ `
  mutation RequestBankLinkStepUp {
    requestBankLinkStepUp {
      sent
      maskedPhone
      expiresInSeconds
    }
  }
`;

const verifyBankLinkStepUpMutation = /* GraphQL */ `
  mutation VerifyBankLinkStepUp($code: String!) {
    verifyBankLinkStepUp(code: $code) {
      verified
      attemptsRemaining
      grant
    }
  }
`;

// --- SEE-221 phone-number change (email-authorize → verify new number → change) ---

export interface PhoneChangeEmailChallenge {
  sent: boolean;
  maskedEmail: string | null;
  expiresInSeconds: number | null;
}

/** Shared shape for the phone-change OTP verifies (email + new number). */
export interface StepUpVerifyResult {
  verified: boolean;
  attemptsRemaining: number | null;
  grant: string | null;
}

export interface ChangePhoneNumberResult {
  success: boolean;
  phoneNumber: string | null;
}

const requestPhoneChangeEmailOtpMutation = /* GraphQL */ `
  mutation RequestPhoneChangeEmailOtp {
    requestPhoneChangeEmailOtp {
      sent
      maskedEmail
      expiresInSeconds
    }
  }
`;

const verifyPhoneChangeEmailOtpMutation = /* GraphQL */ `
  mutation VerifyPhoneChangeEmailOtp($code: String!) {
    verifyPhoneChangeEmailOtp(code: $code) {
      verified
      attemptsRemaining
      grant
    }
  }
`;

const requestNewPhoneOtpMutation = /* GraphQL */ `
  mutation RequestNewPhoneOtp($phoneNumber: String!) {
    requestNewPhoneOtp(phoneNumber: $phoneNumber) {
      sent
      maskedPhone
      expiresInSeconds
    }
  }
`;

const verifyNewPhoneOtpMutation = /* GraphQL */ `
  mutation VerifyNewPhoneOtp($code: String!, $phoneNumber: String!) {
    verifyNewPhoneOtp(code: $code, phoneNumber: $phoneNumber) {
      verified
      attemptsRemaining
      grant
    }
  }
`;

const changePhoneNumberMutation = /* GraphQL */ `
  mutation ChangePhoneNumber($phoneNumber: String!, $emailGrant: String!, $smsGrant: String!) {
    changePhoneNumber(phoneNumber: $phoneNumber, emailGrant: $emailGrant, smsGrant: $smsGrant) {
      success
      phoneNumber
    }
  }
`;

const checkSSODomainQuery = /* GraphQL */ `
  query CheckSSODomain($input: CheckSSODomainInput!) {
    checkSSODomain(input: $input) {
      isSSO
      providerName
    }
  }
`;

// --- SSO Configuration Operations ---
// GraphQL operations and types are defined in graphql_custom/sso/
// but we inline the queries/mutations here to match the service pattern.

const getSSOSetupQuery = /* GraphQL */ `
  query GetSSOSetup($paidolId: String!) {
    getSSOSetup(paidolId: $paidolId) {
      id
      paidolId
      providerName
      metadataUrl
      domains {
        domain
        verificationToken
        verified
      }
      status
      acsUrl
      entityId
      createdBy
      updatedBy
      createdAt
      updatedAt
      linkingStatus
      linkingNewlyLinked
      linkingAlreadyLinked
      linkingStartedAt
      linkingFinishedAt
      linkingError
    }
  }
`;

const ssoConfigurationFields = /* GraphQL */ `
  id
  paidolId
  providerName
  metadataUrl
  status
  acsUrl
  entityId
  domains {
    domain
    verificationToken
    verified
  }
  createdBy
  updatedBy
  createdAt
  updatedAt
  linkingStatus
  linkingNewlyLinked
  linkingAlreadyLinked
  linkingStartedAt
  linkingFinishedAt
  linkingError
`;

const initiateSSOSetupMutation = /* GraphQL */ `
  mutation InitiateSSOSetup($input: InitiateSSOSetupInput!) {
    initiateSSOSetup(input: $input) {
      success
      error
      configuration { ${ssoConfigurationFields} }
    }
  }
`;

const addSSODomainMutation = /* GraphQL */ `
  mutation AddSSODomain($input: AddSSODomainInput!) {
    addSSODomain(input: $input) {
      success
      error
      configuration { ${ssoConfigurationFields} }
    }
  }
`;

const removeSSODomainMutation = /* GraphQL */ `
  mutation RemoveSSODomain($input: RemoveSSODomainInput!) {
    removeSSODomain(input: $input) {
      success
      error
      configuration { ${ssoConfigurationFields} }
    }
  }
`;

const verifySSODomainMutation = /* GraphQL */ `
  mutation VerifySSODomain($input: VerifySSODomainInput!) {
    verifySSODomain(input: $input) {
      success
      verified
      status
      expectedHost
      expectedValue
      actualValues
      checkedAt
      error
    }
  }
`;

const validateSSOMetadataMutation = /* GraphQL */ `
  mutation ValidateSSOMetadata($input: ValidateSSOMetadataInput!) {
    validateSSOMetadata(input: $input) {
      success
      valid
      entityId
      ssoUrl
      ssoBinding
      certificateFingerprint
      error
    }
  }
`;

const saveSSOProviderMutation = /* GraphQL */ `
  mutation SaveSSOProvider($input: SaveSSOProviderInput!) {
    saveSSOProvider(input: $input) {
      success
      error
      configuration { ${ssoConfigurationFields} }
    }
  }
`;

const activateSSOMutation = /* GraphQL */ `
  mutation ActivateSSO($input: ActivateSSOInput!) {
    activateSSO(input: $input) {
      success
      error
      newlyLinkedUserCount
      alreadyLinkedUserCount
      configuration { ${ssoConfigurationFields} }
    }
  }
`;

const deactivateSSOMutation = /* GraphQL */ `
  mutation DeactivateSSO($input: DeactivateSSOInput!) {
    deactivateSSO(input: $input) {
      success
      error
      configuration { ${ssoConfigurationFields} }
    }
  }
`;

const relinkSSOUsersMutation = /* GraphQL */ `
  mutation RelinkSSOUsers($input: RelinkSSOUsersInput!) {
    relinkSSOUsers(input: $input) {
      success
      error
      configuration { ${ssoConfigurationFields} }
    }
  }
`;

const resolveEmailToUsernameQuery = /* GraphQL */ `
  query ResolveEmailToUsername($input: ResolveEmailToUsernameInput!) {
    resolveEmailToUsername(input: $input) {
      statusCode
      body
    }
  }
`;

const v2UserHealthCheckQuery = /* GraphQL */ `
  query V2UserHealthCheck($input: String) {
    v2UserHealthCheck(input: $input)
  }
`;

// --- Service ---

/**
 * Service for v2User operations via the CDK AppSync API.
 *
 * Follows the same pattern as PaidolService: uses fetch() + Cognito JWT
 * to call the CDK-managed AppSync endpoint (REACT_APP_CDK_APPSYNC_ENDPOINT).
 */
export class UserService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  async listFeatureFlags(email: string, paidolId: string): Promise<FeatureFlag[]> {
    const result = await this.execute<{ listFeatureFlags: FeatureFlag[] }>(
      listFeatureFlagsQuery,
      { email, paidolId },
      'listFeatureFlags'
    );
    return result.listFeatureFlags ?? [];
  }

  /**
   * Loads the whitelisted "public" feature flags. Tries Cognito JWT first
   * (authenticated users between login and paidol load), falls back to
   * anonymous IAM signing for pre-auth onboarding users. The schema accepts
   * both auth modes via `@aws_iam @aws_cognito_user_pools`.
   */
  async listPublicFeatureFlags(): Promise<FeatureFlag[]> {
    try {
      const result = await this.execute<{ listPublicFeatureFlags: FeatureFlag[] }>(
        listPublicFeatureFlagsQuery,
        {},
        'listPublicFeatureFlags'
      );
      return result.listPublicFeatureFlags ?? [];
    } catch {
      const result = await this.execute<{ listPublicFeatureFlags: FeatureFlag[] }>(
        listPublicFeatureFlagsQuery,
        {},
        'listPublicFeatureFlags',
        'iam'
      );
      return result.listPublicFeatureFlags ?? [];
    }
  }

  async validateEmailAndUsername(input: ValidateEmailAndUsernameInput): Promise<boolean | null> {
    const result = await this.execute<{ validateEmailAndUsername: boolean | null }>(
      validateEmailAndUsernameQuery,
      { input },
      'validateEmailAndUsername',
      'iam'
    );
    return result.validateEmailAndUsername;
  }

  async checkSSODomain(input: CheckSSODomainInput): Promise<CheckSSODomainResponse> {
    const result = await this.execute<{ checkSSODomain: CheckSSODomainResponse }>(
      checkSSODomainQuery,
      { input },
      'checkSSODomain',
      'iam'
    );
    return result.checkSSODomain ?? { isSSO: false, providerName: null };
  }

  async resolveEmailToUsername(input: ResolveEmailToUsernameInput): Promise<BasicHTTPResponse> {
    const result = await this.execute<{ resolveEmailToUsername: BasicHTTPResponse }>(
      resolveEmailToUsernameQuery,
      { input },
      'resolveEmailToUsername',
      'iam'
    );
    return result.resolveEmailToUsername ?? { statusCode: 500, body: 'Unknown error' };
  }

  async listPaidolUsersFiltered(
    input: ListPaidolUsersFilteredInput
  ): Promise<ListPaidolUsersFilteredPayload> {
    const result = await this.execute<{ listPaidolUsersFiltered: ListPaidolUsersFilteredPayload }>(
      listPaidolUsersFilteredQuery,
      { input },
      'listPaidolUsersFiltered'
    );
    return parseAwsJsonFields(result.listPaidolUsersFiltered);
  }

  async listPaidolUserByUserId(input: ListPaidolUserByUserIdInput): Promise<ListPaidolUserByUserIdPayload> {
    const result = await this.execute<{ listPaidolUserByUserId: ListPaidolUserByUserIdPayload }>(
      listPaidolUserByUserIdQuery,
      { input },
      'listPaidolUserByUserId'
    );
    return parseAwsJsonFields(result.listPaidolUserByUserId);
  }

  async searchPaidolsAsSupport(input: SearchPaidolsAsSupportInput): Promise<SearchPaidolsAsSupportPayload> {
    const result = await this.execute<{ searchPaidolsAsSupport: SearchPaidolsAsSupportPayload }>(
      searchPaidolsAsSupportQuery,
      { input },
      'searchPaidolsAsSupport'
    );
    return parseAwsJsonFields(result.searchPaidolsAsSupport);
  }

  /** SEE-1846 — change a member's phone number. `phoneNumber` is E.164, e.g. +16785550123. */
  async adminUpdateMemberPhone(
    paidolUserId: string,
    phoneNumber: string
  ): Promise<AdminUpdateMemberPhonePayload> {
    const result = await this.execute<{ adminUpdateMemberPhone: AdminUpdateMemberPhonePayload }>(
      adminUpdateMemberPhoneMutation,
      { input: { paidolUserId, phoneNumber } },
      'adminUpdateMemberPhone'
    );
    return result.adminUpdateMemberPhone;
  }

  /** SEE-271 — confirm a "Not signed up yet" member's unconfirmed sign-up. */
  async adminVerifyMember(paidolUserId: string): Promise<AdminVerifyMemberPayload> {
    const result = await this.execute<{ adminVerifyMember: AdminVerifyMemberPayload }>(
      adminVerifyMemberMutation,
      { input: { paidolUserId } },
      'adminVerifyMember'
    );
    return result.adminVerifyMember;
  }

  async removeUser(input: RemoveUserInput): Promise<RemoveUserPayload> {
    const result = await this.execute<{ removeUser: RemoveUserPayload }>(
      removeUserMutation,
      { input },
      'removeUser'
    );
    return result.removeUser;
  }

  /**
   * SEE-856 D1 — admin-only privileged-field write on another paidol member.
   * Throws on 4xx with the body string as error message; returns the updated
   * member dict (parsed from BasicHTTPResponse.body) on success.
   */
  async adminUpdateMember(input: AdminUpdateMemberInput): Promise<Record<string, unknown>> {
    const result = await this.execute<{ adminUpdateMember: BasicHttpResponse }>(
      adminUpdateMemberMutation,
      { input },
      'adminUpdateMember'
    );
    const payload = result.adminUpdateMember;
    if (!payload || payload.statusCode == null) {
      throw new Error('adminUpdateMember returned no payload');
    }
    if (payload.statusCode !== 200) {
      throw new Error(payload.body || `adminUpdateMember failed (${payload.statusCode})`);
    }
    if (!payload.body) {
      return {};
    }
    return JSON.parse(payload.body) as Record<string, unknown>;
  }

  /**
   * SEE-1312 — admin read of a member's notification preferences (the read
   * half of adminUpdateNotificationPreferences). Resolves to the stored row,
   * or null when the member has no row yet so the caller renders defaults.
   * Throws on 4xx with the body string.
   */
  async adminGetNotificationPreferences(
    input: AdminGetNotificationPreferencesInput
  ): Promise<Record<string, unknown> | null> {
    const result = await this.execute<{ adminGetNotificationPreferences: BasicHttpResponse }>(
      adminGetNotificationPreferencesQuery,
      { input },
      'adminGetNotificationPreferences'
    );
    const payload = result.adminGetNotificationPreferences;
    if (!payload || payload.statusCode == null) {
      throw new Error('adminGetNotificationPreferences returned no payload');
    }
    if (payload.statusCode !== 200) {
      throw new Error(payload.body || `adminGetNotificationPreferences failed (${payload.statusCode})`);
    }
    if (!payload.body) {
      return null;
    }
    return JSON.parse(payload.body) as Record<string, unknown> | null;
  }

  async adminSetGroupAdmins(input: AdminSetGroupAdminsInput): Promise<void> {
    const result = await this.execute<{ adminSetGroupAdmins: BasicHttpResponse }>(
      adminSetGroupAdminsMutation,
      { input },
      'adminSetGroupAdmins'
    );
    const payload = result.adminSetGroupAdmins;
    if (!payload || payload.statusCode == null) {
      throw new Error('adminSetGroupAdmins returned no payload');
    }
    if (payload.statusCode !== 200) {
      throw new Error(`adminSetGroupAdmins failed (${payload.statusCode}): ${payload.body}`);
    }
  }

  async adminUpdateNotificationPreferences(
    input: AdminUpdateNotificationPreferencesInput
  ): Promise<Record<string, unknown>> {
    const result = await this.execute<{ adminUpdateNotificationPreferences: BasicHttpResponse }>(
      adminUpdateNotificationPreferencesMutation,
      { input },
      'adminUpdateNotificationPreferences'
    );
    const payload = result.adminUpdateNotificationPreferences;
    if (!payload || payload.statusCode == null) {
      throw new Error('adminUpdateNotificationPreferences returned no payload');
    }
    if (payload.statusCode !== 200) {
      throw new Error(payload.body || `adminUpdateNotificationPreferences failed (${payload.statusCode})`);
    }
    if (!payload.body) {
      return {};
    }
    return JSON.parse(payload.body) as Record<string, unknown>;
  }

  /**
   * SEE-857 D2 — self-service write of the caller's own non-privileged
   * PaidolUser preferences (landing page, column visibility, widget
   * visibility). The Lambda strict-rejects any field outside this set.
   * Throws on 4xx with the body string; returns the updated dict on 200.
   */
  async updateMyPreferences(input: UpdateMyPreferencesInput): Promise<Record<string, unknown>> {
    const result = await this.execute<{ updateMyPreferences: BasicHttpResponse }>(
      updateMyPreferencesMutation,
      { input },
      'updateMyPreferences'
    );
    const payload = result.updateMyPreferences;
    if (!payload || payload.statusCode == null) {
      throw new Error('updateMyPreferences returned no payload');
    }
    if (payload.statusCode !== 200) {
      throw new Error(payload.body || `updateMyPreferences failed (${payload.statusCode})`);
    }
    if (!payload.body) {
      return {};
    }
    return JSON.parse(payload.body) as Record<string, unknown>;
  }

  /**
   * SEE-191: mark one first-run checklist key complete on the caller's Paidol.
   * input.operationName must be 'completeFirstRunChecklist' (the Lambda dispatches on it).
   */
  async completeFirstRunChecklist(input: CompleteChecklistInput): Promise<CompleteChecklistPayload | null> {
    const result = await this.execute<{ completeFirstRunChecklist: CompleteChecklistPayload | null }>(
      completeFirstRunChecklistMutation,
      { input },
      'completeFirstRunChecklist'
    );
    return result.completeFirstRunChecklist;
  }

  /** SEE-1232 row m — the caller's own preferences row, or null if they have none yet. */
  async getMyNotificationPreferences<T>(): Promise<T | null> {
    const result = await this.execute<{ getMyNotificationPreferences: T | null }>(
      getMyNotificationPreferencesQuery,
      {},
      'getMyNotificationPreferences'
    );
    return result.getMyNotificationPreferences;
  }

  /** Errors if the caller already has a row (as Amplify's conditional create did). */
  async createMyNotificationPreferences<T>(input: Record<string, unknown>): Promise<T | null> {
    const result = await this.execute<{ createMyNotificationPreferences: T | null }>(
      createMyNotificationPreferencesMutation,
      { input },
      'createMyNotificationPreferences'
    );
    return result.createMyNotificationPreferences;
  }

  /** Partial update of the caller's row; errors if they have none. */
  async updateMyNotificationPreferences<T>(input: Record<string, unknown>): Promise<T | null> {
    const result = await this.execute<{ updateMyNotificationPreferences: T | null }>(
      updateMyNotificationPreferencesMutation,
      { input },
      'updateMyNotificationPreferences'
    );
    return result.updateMyNotificationPreferences;
  }

  /** SEE-1232 row k — the signed-in user's own profile. Null when there is no row. */
  async getUser<T>(id: string): Promise<T | null> {
    const result = await this.execute<{ getUser: T | null }>(getUserQuery, { id }, 'getUser');
    return result.getUser;
  }

  /** SEE-1232 row l — owner-only profile update; resolves the updated profile. */
  async updateUser<T>(input: Record<string, unknown>): Promise<T | null> {
    const result = await this.execute<{ updateUser: T | null }>(updateUserMutation, { input }, 'updateUser');
    return result.updateUser;
  }

  /**
   * SEE-191: mark one first-run description banner key dismissed on the caller's User row.
   * input.operationName must be 'completeFirstRunBanner'.
   */
  async completeFirstRunBanner(input: CompleteBannerInput): Promise<CompleteBannerPayload | null> {
    const result = await this.execute<{ completeFirstRunBanner: CompleteBannerPayload | null }>(
      completeFirstRunBannerMutation,
      { input },
      'completeFirstRunBanner'
    );
    return result.completeFirstRunBanner;
  }

  async resetUserFilters(input: ResetUserFiltersInput): Promise<ResetUserFiltersPayload> {
    const result = await this.execute<{ resetUserFilters: ResetUserFiltersPayload }>(
      resetUserFiltersMutation,
      { input },
      'resetUserFilters'
    );
    return result.resetUserFilters;
  }

  async countUsersWithCustomFilters(
    input: CountUsersWithCustomFiltersInput
  ): Promise<CountUsersWithCustomFiltersPayload> {
    const result = await this.execute<{
      countUsersWithCustomFilters: CountUsersWithCustomFiltersPayload;
    }>(countUsersWithCustomFiltersMutation, { input }, 'countUsersWithCustomFilters');
    return result.countUsersWithCustomFilters;
  }

  async sendWelcomeSms(input: SendWelcomeSmsInput): Promise<BasicHTTPResponse> {
    const result = await this.execute<{ sendWelcomeSms: BasicHTTPResponse }>(
      sendWelcomeSmsMutation,
      { input },
      'sendWelcomeSms'
    );
    return result.sendWelcomeSms;
  }

  // --- SEE-221 step-up MFA (bank linking) ---

  async requestBankLinkStepUp(): Promise<BankLinkStepUpChallenge> {
    const result = await this.execute<{ requestBankLinkStepUp: BankLinkStepUpChallenge }>(
      requestBankLinkStepUpMutation,
      {},
      'requestBankLinkStepUp'
    );
    return result.requestBankLinkStepUp;
  }

  async verifyBankLinkStepUp(code: string): Promise<BankLinkStepUpResult> {
    const result = await this.execute<{ verifyBankLinkStepUp: BankLinkStepUpResult }>(
      verifyBankLinkStepUpMutation,
      { code },
      'verifyBankLinkStepUp'
    );
    return result.verifyBankLinkStepUp;
  }

  // --- SEE-221 phone-number change ---

  async requestPhoneChangeEmailOtp(): Promise<PhoneChangeEmailChallenge> {
    const result = await this.execute<{ requestPhoneChangeEmailOtp: PhoneChangeEmailChallenge }>(
      requestPhoneChangeEmailOtpMutation,
      {},
      'requestPhoneChangeEmailOtp'
    );
    return result.requestPhoneChangeEmailOtp;
  }

  async verifyPhoneChangeEmailOtp(code: string): Promise<StepUpVerifyResult> {
    const result = await this.execute<{ verifyPhoneChangeEmailOtp: StepUpVerifyResult }>(
      verifyPhoneChangeEmailOtpMutation,
      { code },
      'verifyPhoneChangeEmailOtp'
    );
    return result.verifyPhoneChangeEmailOtp;
  }

  async requestNewPhoneOtp(phoneNumber: string): Promise<BankLinkStepUpChallenge> {
    const result = await this.execute<{ requestNewPhoneOtp: BankLinkStepUpChallenge }>(
      requestNewPhoneOtpMutation,
      { phoneNumber },
      'requestNewPhoneOtp'
    );
    return result.requestNewPhoneOtp;
  }

  async verifyNewPhoneOtp(code: string, phoneNumber: string): Promise<StepUpVerifyResult> {
    const result = await this.execute<{ verifyNewPhoneOtp: StepUpVerifyResult }>(
      verifyNewPhoneOtpMutation,
      { code, phoneNumber },
      'verifyNewPhoneOtp'
    );
    return result.verifyNewPhoneOtp;
  }

  async changePhoneNumber(
    phoneNumber: string,
    emailGrant: string,
    smsGrant: string
  ): Promise<ChangePhoneNumberResult> {
    const result = await this.execute<{ changePhoneNumber: ChangePhoneNumberResult }>(
      changePhoneNumberMutation,
      { phoneNumber, emailGrant, smsGrant },
      'changePhoneNumber'
    );
    return result.changePhoneNumber;
  }

  // --- SSO Configuration ---

  async getSSOSetup(paidolId: string): Promise<SSOConfiguration | null> {
    const result = await this.execute<{ getSSOSetup: SSOConfiguration | null }>(
      getSSOSetupQuery,
      { paidolId },
      'getSSOSetup'
    );
    return result.getSSOSetup;
  }

  async initiateSSOSetup(input: { paidolId: string; domains: string[] }): Promise<SSOOperationResponse> {
    const result = await this.execute<{ initiateSSOSetup: SSOOperationResponse }>(
      initiateSSOSetupMutation,
      { input },
      'initiateSSOSetup'
    );
    return result.initiateSSOSetup;
  }

  async addSSODomain(input: { paidolId: string; domain: string }): Promise<SSOOperationResponse> {
    const result = await this.execute<{ addSSODomain: SSOOperationResponse }>(
      addSSODomainMutation,
      { input },
      'addSSODomain'
    );
    return result.addSSODomain;
  }

  async removeSSODomain(input: { paidolId: string; domain: string }): Promise<SSOOperationResponse> {
    const result = await this.execute<{ removeSSODomain: SSOOperationResponse }>(
      removeSSODomainMutation,
      { input },
      'removeSSODomain'
    );
    return result.removeSSODomain;
  }

  async verifySSODomain(input: { paidolId: string; domain: string }): Promise<VerifySSODomainResponse> {
    const result = await this.execute<{ verifySSODomain: VerifySSODomainResponse }>(
      verifySSODomainMutation,
      { input },
      'verifySSODomain'
    );
    return result.verifySSODomain;
  }

  async saveSSOProvider(input: { paidolId: string; metadataUrl: string }): Promise<SSOOperationResponse> {
    const result = await this.execute<{ saveSSOProvider: SSOOperationResponse }>(
      saveSSOProviderMutation,
      { input },
      'saveSSOProvider'
    );
    return result.saveSSOProvider;
  }

  async validateSSOMetadata(input: {
    paidolId: string;
    metadataUrl: string;
  }): Promise<SSOMetadataValidationResponse> {
    const result = await this.execute<{
      validateSSOMetadata: SSOMetadataValidationResponse;
    }>(validateSSOMetadataMutation, { input }, 'validateSSOMetadata');
    return result.validateSSOMetadata;
  }

  async activateSSO(input: {
    paidolId: string;
    domains?: { domain: string; verificationToken: string }[];
    metadataUrl?: string;
  }): Promise<SSOOperationResponse> {
    const result = await this.execute<{ activateSSO: SSOOperationResponse }>(
      activateSSOMutation,
      { input },
      'activateSSO'
    );
    return result.activateSSO;
  }

  async deactivateSSO(input: { paidolId: string }): Promise<SSOOperationResponse> {
    const result = await this.execute<{ deactivateSSO: SSOOperationResponse }>(
      deactivateSSOMutation,
      { input },
      'deactivateSSO'
    );
    return result.deactivateSSO;
  }

  async relinkSSOUsers(input: { paidolId: string }): Promise<SSOOperationResponse> {
    const result = await this.execute<{ relinkSSOUsers: SSOOperationResponse }>(
      relinkSSOUsersMutation,
      { input },
      'relinkSSOUsers'
    );
    return result.relinkSSOUsers;
  }

  async v2UserHealthCheck(input: string): Promise<string> {
    const result = await this.execute<{ v2UserHealthCheck: string }>(
      v2UserHealthCheckQuery,
      { input },
      'v2UserHealthCheck'
    );
    return result.v2UserHealthCheck;
  }

  // --- Private helpers ---

  private async execute<T>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string,
    authMode: AuthMode = 'cognito'
  ): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'user', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const body = JSON.stringify({ query, variables });
      let response: Response;

      if (authMode === 'iam') {
        response = await this.executeWithIamAuth(body);
      } else {
        const session = await Auth.currentSession();
        const jwt = session.getIdToken().getJwtToken();
        response = await fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: jwt,
          },
          body,
        });
      }

      const json = (await response.json()) as GraphQLResult<T> & {
        errors?: Array<{ message: string }>;
      };

      reportCdkRefusal(json?.errors, { authMode, operationName });

      if (!response.ok) {
        const message = json?.errors?.map((e) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }

      if (json.errors && json.errors.length > 0) {
        const errorMessage = json.errors.map((e) => e.message).join(', ');
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      if (!json.data) {
        throw new Error('No data returned from operation');
      }

      return json.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to execute ${operationName}:`, error);

      if (error instanceof Error) {
        captureException(error, {
          tags: { area: 'user', type: isNetworkTransportError(error) ? 'network-transport' : 'graphql' },
          extra: { operation: operationName, endpoint: this.cdkAppSyncEndpoint },
        });
        throw error;
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  private async executeWithIamAuth(body: string): Promise<Response> {
    const credentials = await Auth.currentCredentials();
    const { accessKeyId, secretAccessKey, sessionToken } = credentials;

    const url = new URL(this.cdkAppSyncEndpoint);
    // Extract region from AppSync endpoint: *.appsync-api.{region}.amazonaws.com
    const regionMatch = url.hostname.match(/\.appsync-api\.([^.]+)\./);
    const region = regionMatch?.[1] ?? 'us-east-2';

    const request = {
      method: 'POST',
      url: this.cdkAppSyncEndpoint,
      headers: {
        'content-type': 'application/json',
        host: url.host,
      },
      data: body,
    };

    const signedRequest = Signer.sign(
      request,
      { access_key: accessKeyId, secret_key: secretAccessKey, session_token: sessionToken },
      { region, service: 'appsync' }
    );

    return fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
      method: 'POST',
      headers: signedRequest.headers,
      body,
    });
  }
}

// Export a singleton instance for convenience
export const userService = new UserService();
